import { Suspense } from "react";
import { Helmet } from "react-helmet-async";

import { SourceType, SubscriptionStatus } from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Heading } from "@/components/elements/Typography";
import {
  SyncsBlockedBookMeetingButton,
  syncsBlockedMessages,
} from "@/components/modules/SyncsBlocked";
import { TaskActionBar } from "@/components/modules/TaskActionBar";
import { UpgradeNowButton } from "@/components/modules/UpgradeNowButton";
import { useSubscriptionStatus } from "@/features/subscription";
import cn from "@/helpers/classNames";
import HRWithText from "@/pages/Setup/components/HRWithText";
import { useToast } from "@/providers/ToastProvider";
import { useCurrentAccount } from "@/providers/account";
import { useNavigateWithSlug } from "@/routes";
import { CreateSyncContainer } from "@/widgets/syncs/reverse-etl";
import { ErrorBoundary } from "@sentry/react";
import { useSearch } from "@tanstack/react-location";

export default function CreateSyncPage(props: { pageTitle?: string }) {
  const searchParams = useSearch<{
    Search: {
      redirect: string;
      integration_id: string;
      connection_id: string;
      source_id: string;
      source_type: SourceType;
    };
  }>();

  const navigate = useNavigateWithSlug();
  const toast = useToast();
  const account = useCurrentAccount();
  return (
    <>
      <Helmet>
        <title>Create Reverse-ETL Sync</title>
      </Helmet>
      <main className="relative flex flex-1 flex-col">
        <div
          className={cn(
            "mx-auto flex w-full max-w-8xl grow flex-col overflow-auto px-4 sm:px-6 md:px-8",
          )}
        >
          <div className="mx-auto mb-2 w-full max-w-4xl px-6 pt-8">
            <Heading as="h1" className="text-lg font-normal">
              {props.pageTitle ?? `Create Reverse ETL Sync`}
            </Heading>
          </div>
          <div className="grow">
            <ErrorBoundary fallback={<div>Failed to load</div>}>
              <Suspense
                fallback={
                  <div className="flex justify-center py-20">
                    <LoadingSpinner />
                  </div>
                }
              >
                <CreateSyncContainer
                  initialProps={{
                    integrationId: searchParams.integration_id,
                    connectionId: searchParams.connection_id,
                    sourceId: searchParams.source_id,
                    sourceType: searchParams.source_type,
                  }}
                  onSetupStarted={() => {
                    navigate({
                      search(prev) {
                        return {
                          ...prev,
                          integration_id: undefined,
                          connection_id: undefined,
                        };
                      },
                    });
                  }}
                  onSetupCompleted={(sync) => {
                    toast(
                      "Sync created successfully",
                      "The sync is now running",
                      "success",
                    );
                    if (searchParams.redirect) {
                      navigate({ to: searchParams.redirect }, false);
                    } else {
                      navigate({ to: `/reverse-etl/${sync.id}` });
                    }
                  }}
                  onSetupError={(error) => {
                    toast("Failed to create sync", error.message, "error");
                  }}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
        {account.accountSettings.deactivateReverseELTSyncs && (
          <div className="absolute inset-0 z-10 flex h-full w-full items-center justify-center bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:bg-gray-900/95 dark:supports-[backdrop-filter]:bg-gray-900/60">
            <SyncsBlockedMessage />
          </div>
        )}
      </main>
      <TaskActionBar
        className="max-w-4xl px-2"
        onCancelClicked={
          searchParams.redirect
            ? () => navigate({ to: searchParams.redirect })
            : undefined
        }
      />
    </>
  );
}

function SyncsBlockedMessage() {
  const { status } = useSubscriptionStatus();
  return (
    <div className="flex flex-col items-center gap-4 text-muted-foreground">
      <h3 className="text-lg font-medium">
        {syncsBlockedMessages.REVERSE_ETL_SYNCS_BLOCKED_HEADING}
      </h3>
      {status === SubscriptionStatus.Freemium ? (
        <div className="flex flex-col items-center gap-4">
          <p>
            To keep using our paid features, upgrade to one of our paid plans.
          </p>
          <UpgradeNowButton source="new export page" />
          <HRWithText>Or</HRWithText>
          <SyncsBlockedBookMeetingButton />
        </div>
      ) : (
        <>
          <p>{syncsBlockedMessages.REVERSE_ETL_SYNCS_BLOCKED_BODY}</p>
          <SyncsBlockedBookMeetingButton />
        </>
      )}
    </div>
  );
}
